/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from "react";
import { Suspense, useCallback, useMemo, useRef } from "react";
import type { ShouldRevalidateFunction } from "react-router";
import {
  Await,
  Outlet,
  useLoaderData,
  useMatches,
  useSearchParams,
} from "react-router";

import { SWR } from "~/.server/caching.server";
import {
  isAuthenticatedUser,
  optionalAuthenticatedUser,
} from "~/.server/sessions.server";
import { getClient } from "~/contentful/contentful.server";
import { deferDataRequest } from "~/lib/utils-server/http.server";

import {
  useContentfulInspectorMode,
  useContentfulLiveUpdates,
} from "@contentful/live-preview/react";

import { Footer } from "~/components/footer/footer";
import { Meganav } from "~/components/meganav/meganav";
import { Boundry } from "~/components/meganav/meganav-nav";
import type { TypeTextBlockSkeleton } from "~/contentful/compiled";
import type { TypeFooterSkeleton } from "~/contentful/compiled/TypeFooter";
import { FOOTER_CONTENT, TOP_BAR_MESSAGES } from "~/contentful/contents";
import { MediaScreenProvider } from "~/contexts/madia-screen";
import type { Customer } from "~/lib/app.types";

import { BasketProvider } from "./resources/basket";
import AddedToBasketFlyout from "./resources/components/added-to-basket-flyout";
import CookieBanner from "./resources/components/cookie-banner";
import { WishlistProvider } from "./resources/wishlist";

export const shouldRevalidate: ShouldRevalidateFunction = ({
  formMethod,
  formAction,
  currentUrl,
}) => {
  if (formMethod === "POST" && formAction?.includes("/basket")) return true;
  if (formMethod === "POST" && formAction?.includes("/login")) return true;
  if (currentUrl.pathname.includes("/logout")) {
    return true;
  }

  return false;
};

export const loader = async ({ request, context }: LoaderArgs) => {
  const url = new URL(request.url);
  const preview = url.searchParams.get("preview");
  const client = getClient(!!preview);
  const [swr] = SWR.stable("footer-data", ["footer", "data", "contentful"]);
  const isLoggedIn = isAuthenticatedUser();
  const user = optionalAuthenticatedUser();
  const [swrTopBarMessage] = SWR.stable("topbar-data", [
    "topbar",
    "data",
    "contentful",
  ]);

  const topBarMessages = swrTopBarMessage(async () => {
    return await client.getEntry<TypeTextBlockSkeleton>(TOP_BAR_MESSAGES);
  }, [TOP_BAR_MESSAGES]);

  const [footerData] = await deferDataRequest(
    swr(async () => {
      return await client.getEntry<TypeFooterSkeleton>(FOOTER_CONTENT);
    }, [FOOTER_CONTENT]),
  );
  return {
    footerData,
    isLoggedIn,
    user: await user,
    topBarMessages,
  };
};

export default function AppLayout() {
  const ContentHeroSlot = useSlot("contentRootHeroSlot");
  const ContentBotomSlot = useSlot("contentRootBottomSlot");
  const { footerData, isLoggedIn, user, topBarMessages } =
    useLoaderData<typeof loader>();
  const liveContent = useContentfulLiveUpdates(footerData);
  const [searchParams] = useSearchParams();
  const preview = !!searchParams.get("preview");
  const contentData = preview ? liveContent : footerData;
  const boundryRef = useRef<HTMLDivElement>(null!);
  const inspectorProps = useContentfulInspectorMode();
  const contentMessages = useContentfulLiveUpdates(topBarMessages);

  return (
    <MediaScreenProvider>
      <BasketProvider>
        <WishlistProvider>
          <Meganav
            boundryRef={boundryRef}
            isLoggedIn={isLoggedIn}
            user={user as Customer | undefined}
            messages={contentMessages}
          />
          <div id="blurrable-content">
            {/* HERO Banner */}
            <ContentHeroSlot />
            {/* This boundry tells the many that we are past the main hero (then it adds background to the menu) */}
            <Boundry ref={boundryRef} />
            {/* -- / -- HERO Banner end */}
            <div>
              <Outlet />
            </div>
            {/* Footer --- */}
            <ContentBotomSlot />
            <Suspense fallback={null}>
              <Await resolve={contentData}>
                {contentData => (
                  <span
                    {...inspectorProps({
                      entryId: contentData.sys.id,
                      fieldId: "name",
                    })}
                  >
                    {" "}
                    <Footer content={contentData} />{" "}
                  </span>
                )}
              </Await>
            </Suspense>
          </div>
          <CookieBanner />
          <AddedToBasketFlyout />
        </WishlistProvider>
      </BasketProvider>
    </MediaScreenProvider>
  );
}

const useSlot = (
  contentKey: keyof AppHandle,
): (() => React.JSX.Element | null) => {
  const matches = useMatches();
  const match = useMemo(
    () => matches.reverse().find(match => (match as any).handle?.[contentKey]),
    [matches, contentKey],
  );
  return useCallback(() => {
    if (!match) return null;
    return (match as any).handle?.[contentKey]?.(match.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.pathname]);
};
export const useRouteHandle = (): AppHandle | undefined => {
  const matches = useMatches();
  return useMemo(
    () =>
      (matches as any).reverse().find((match: { handle: any }) => match.handle)
        ?.handle,
    [matches],
  );
};
